import React, { useState } from 'react';
import { Box, Button, Collapse, Typography } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import type { DashboardFilters } from '../Dashboard/DashboardFilterBar';
import { fetchSeverityBrands } from './operationDashboardApi';
import type { Quadrant } from './types';

interface Props {
  quadrant: Quadrant;
  severity: 'red' | 'orange' | 'green';
  filters: DashboardFilters;
}

const SeverityBrandDropdown: React.FC<Props> = ({ quadrant, severity, filters }) => {
  const [open, setOpen] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['op-severity-brands', quadrant, filters],
    queryFn: () => fetchSeverityBrands(quadrant, filters),
    enabled: open,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 25_000,
  });

  // Branch on severity so each row is concretely typed (no casts): the green
  // slice is GreenBrandRow[], the red/orange slice is RedOrangeBrandRow[].
  const items: { brand: string; value: React.ReactNode }[] =
    severity === 'green'
      ? (data?.levels.green ?? []).map((r) => ({
          brand: r.brand,
          value: (
            <Box component="span" sx={{ color: 'var(--color-text-muted)' }} title={`${r.sku_count} SKUs`}>
              0 issues
            </Box>
          ),
        }))
      : (data?.levels[severity] ?? []).map((r) => ({
          brand: r.brand,
          value: (
            <Box component="span" sx={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>
              {r.total_count}
              <Box component="span" sx={{ color: 'var(--color-text-muted)', fontWeight: 400, ml: 0.5 }}>
                · {r.issue_count} {r.issue_count === 1 ? 'rule' : 'rules'}
              </Box>
            </Box>
          ),
        }));

  return (
    <Box sx={{ mt: 0.5 }}>
      <Button
        size="small"
        onClick={() => setOpen((v) => !v)}
        endIcon={<ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />}
        sx={{ textTransform: 'none', fontSize: '12px', color: 'var(--color-text-secondary, #4B4B47)', px: 0.5 }}
      >
        Top 10 brands
      </Button>
      <Collapse in={open} unmountOnExit={false}>
        {isLoading ? (
          <Box sx={{ height: 48, borderRadius: 'var(--radius-md, 6px)', bgcolor: 'var(--color-bg-tertiary, #EDEDE8)' }} />
        ) : error ? (
          <Typography sx={{ fontSize: '12px', color: 'var(--color-danger, #DC2626)' }}>Couldn&apos;t load brands — retry</Typography>
        ) : items.length === 0 ? (
          <Typography sx={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>No brands</Typography>
        ) : (
          <Box component="ol" sx={{ m: 0, pl: 2.5, fontSize: '12px' }}>
            {items.map((it) => (
              <Box component="li" key={it.brand} sx={{ py: 0.25, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Box component="span" sx={{ color: 'var(--color-text-primary)' }}>{it.brand}</Box>
                {it.value}
              </Box>
            ))}
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default SeverityBrandDropdown;
